import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import { TRIO_COLUMN_WIDTH_MD } from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import * as S from './TrioImageModule.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { Image } from '@rocco/components/image';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail-mobile/detail-modules/DetailModules.style';
import LazyImageModule from '../detail-modules/LazyImageModule';

/** Number of modules to render eagerly (above-fold) */
const EAGER_COUNT = 2;

interface TrioImageModuleProps {
    columns: ImageColumnProps[];
    /** Index of this module for eager loading determination */
    moduleIndex?: number;
}

/**
 * Mobile-optimised Page Builder module for displaying three images in a grid layout.
 *
 * Uses mobile-specific image sizes (~138px per image) for better performance.
 * The module displays three images side by side on desktop and stacked on mobile.
 * Each image can have captions, tags, and interactive elements like paint picker integration.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/trio-image-module/TrioImageModule.tsx}
 */
const TrioImageModule = ({
    columns,
    moduleIndex = 0,
}: TrioImageModuleProps) => {
    const { mobile } = useAppDisplayModeContext();
    const isEager = moduleIndex < EAGER_COUNT;

    const { css } = useStyles({
        columns: columns.filter((c) => mobile && c.imageUrl).length,
        mobile,
    });

    return (
        <div
            className={css(S.TrioImageModuleContainer, ImageModuleExtraSpacing)}
        >
            {columns.map((column) => {
                const isMedium = column.dimension.width >= TRIO_COLUMN_WIDTH_MD;
                if (mobile && !column.imageUrl) {
                    return null;
                }

                if (!column?.imageUrl) {
                    return null;
                }

                return (
                    <LazyImageModule
                        key={column.id}
                        className={css(S.TrioImageContainerImage(isMedium))}
                        dimension={column.dimension}
                        moduleIndex={moduleIndex}
                        eagerCount={EAGER_COUNT}
                    >
                        <DetailSingleImageShell
                            caption={column.caption}
                            dimension={column.dimension}
                            libraryImage={column?.libraryImage}
                            tags={column.tags}
                            uniqueApprovedTags={column.uniqueApprovedTags}
                            imageURL={column.imageUrl}
                        >
                            <Image
                                src={column.imageUrl}
                                width={
                                    column.dimension.mobileWidth ?? undefined
                                }
                                height={
                                    column.dimension.mobileHeight ?? undefined
                                }
                                fill
                                fit="cover"
                                loading={isEager ? 'eager' : 'lazy'}
                                fetchPriority={isEager ? 'high' : 'low'}
                                className="!rounded-none"
                                sizes={getImagesSizes({
                                    desktop: column.dimension.width,
                                    mobile: column.dimension.mobileWidth,
                                })}
                                alt={
                                    column.caption ||
                                    column?.libraryImage?.Title ||
                                    ''
                                }
                            />
                        </DetailSingleImageShell>
                    </LazyImageModule>
                );
            })}
        </div>
    );
};

export default TrioImageModule;
